import { modalityText } from './positionText';

const RANGE =
    /^(\d{1,2}:\d{2})\s*(AM|PM)?\s*(?:-|–|to)\s*(\d{1,2}:\d{2})\s*(AM|PM)?$/i;

/** "9:00 AM - 9:50 AM" reads "9:00 to 9:50 AM"; anything else is kept as typed. */
export function timeRangeText(time) {
    const text = String(time ?? '').trim();
    const match = RANGE.exec(text);
    if (!match) return text;
    const [, start, startMeridiem, end, endMeridiem] = match;
    const from =
        startMeridiem &&
        startMeridiem.toUpperCase() !== endMeridiem?.toUpperCase()
            ? `${start} ${startMeridiem.toUpperCase()}`
            : start;
    return `${from} to ${end}${endMeridiem ? ` ${endMeridiem.toUpperCase()}` : ''}`;
}

const placesText = (count) =>
    count === 1 ? '1 place open' : `${count} places open`;

function meetingFact(meeting, showType, notePlace) {
    const when = [meeting.days, timeRangeText(meeting.time)]
        .filter(Boolean)
        .join(' ');
    const typed =
        showType && meeting.type && when
            ? `${meeting.type[0].toUpperCase()}${meeting.type.slice(1)}: ${when}`
            : when;
    return [typed, meeting.room || (notePlace ? 'room not set yet' : '')]
        .filter(Boolean)
        .join(', ');
}

/**
 * When and where a position meets, for the first part of the key-facts line.
 * Meetings with no days and time say why (an online section may have none)
 * and still show a room that is known.
 */
export function scheduleFact(position, { notePlace = false } = {}) {
    const meetings = position?.meetings || [];
    const timed = meetings.filter((m) => m.days || m.time);
    if (timed.length > 0) {
        const showType = timed.length > 1;
        const noteRoom = notePlace && position?.modality !== 'online';
        return timed.map((m) => meetingFact(m, showType, noteRoom)).join('; ');
    }
    const rooms = [...new Set(meetings.map((m) => m.room).filter(Boolean))];
    const unknown =
        position?.modality === 'online'
            ? 'Online, no set meeting time'
            : 'Meeting times not set yet';
    return [unknown, ...rooms].join(', ');
}

/**
 * One line of the facts students compare: "MWF 9:00 to 9:50 AM, Science 1050 |
 * In person | 10 h/week | 1 place open". A part that is unknown is left out
 * (hours when the program sets none, places when the row carries no count).
 * `notePlace` says "room not set yet" for an in-person meeting with no room,
 * for the confirmations where the student commits to being there.
 */
export function positionKeyFacts(
    position,
    { hoursPerWeek, notePlace = false } = {}
) {
    const hours = hoursPerWeek ?? position?.hoursPerWeek;
    return [
        scheduleFact(position, { notePlace }),
        modalityText(position?.modality),
        hours != null ? `${hours} h/week` : '',
        Number.isFinite(position?.openSlots)
            ? placesText(position.openSlots)
            : '',
    ]
        .filter(Boolean)
        .join(' | ');
}
